// Minimal API surface for the widget host page — login, /me, and a
// status-flip endpoint. Everything else (greetings, voicemail, agents,
// audit) lives in the admin portal.

const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export type AgentStatus = "available" | "away";

export interface AgentMe {
  agent: {
    id: string;
    email: string;
    name: string | null;
    role: "agent" | "admin";
    status: AgentStatus;
    enabled: boolean;
    quota: { cap: number | null; used: number; overCap: boolean };
  };
  sip: { wsServer: string; user: string; password: string; domain: string; displayName: string };
  iceServers: RTCIceServer[];
}

export interface LoginResponse {
  token: string;
  agent: { id: string; email: string; role: "agent" | "admin"; name: string | null };
}

export interface TurnToken {
  urls: string[];
  username: string;
  credential: string;
  ttl: number;
  expiresAt: number;
}

class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit & { auth?: string } = {}): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init.auth ? { authorization: `Bearer ${init.auth}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, `${res.status} ${path}: ${body}`);
  }
  return (await res.json()) as T;
}

export const api = {
  async login(email: string, password: string): Promise<LoginResponse> {
    return request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },
  async me(token: string): Promise<AgentMe> {
    return request<AgentMe>("/api/agents/me", { auth: token });
  },
  async setMyStatus(token: string, status: AgentStatus): Promise<{ status: AgentStatus }> {
    return request("/api/agents/me/status", {
      method: "PATCH",
      auth: token,
      body: JSON.stringify({ status }),
    });
  },
  /** plan 57 C-2 : la trace d'un clic « Décrocher » (tentative, puis issue) — l'appelant n'attend pas la réponse. */
  async answerEvent(token: string, ev: Record<string, unknown>): Promise<{ ok: true; callId: string | null }> {
    return request("/api/agents/me/answer-events", { method: "POST", auth: token, body: JSON.stringify(ev) });
  },
  async turnToken(token: string): Promise<TurnToken> {
    // Body "{}" (not empty) so Fastify's JSON parser doesn't reject the
    // POST with FST_ERR_CTP_EMPTY_JSON_BODY — `request` always sets
    // content-type: application/json.
    return request<TurnToken>("/api/turn-token", {
      method: "POST",
      auth: token,
      body: "{}",
    });
  },
};

export { ApiError };
